import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "wouter";
import { usePageTitle } from "@/hooks/use-page-title";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, Cell, Legend,
} from "recharts";
import {
  Users, DollarSign, TrendingUp, TrendingDown, FileText, Receipt,
  CreditCard, Mail, MailX, Zap, AlertTriangle, Clock, Activity,
  ArrowRight, Server, Database, RefreshCw,
} from "lucide-react";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

type DateRange = "7d" | "30d" | "90d" | "thisMonth" | "custom";

interface AnalyticsData {
  billing: {
    totalUsers: number;
    activeUsersLast7Days: number;
    paidUsers: number;
    newSignupsInRange: number;
    newPaidInRange: number;
    churnInRange: number;
    mrrCents: number;
    arrCents: number;
    revenueTotalInRangeCents: number;
    usersByPlan: Array<{ plan: string; count: number }>;
    failedPayments: Array<{ id: string; customer: string; email: string; amount: number; currency: string; date: string; status: string }>;
    mrrByMonth: Array<{ month: string; mrr: number }>;
  };
  advanced: {
    arpu: number;
    ltv: number;
    cohortRetention: Array<{
      month: string;
      total: number;
      ret1m: number | null;
      ret2m: number | null;
      ret3m: number | null;
      ret6m: number | null;
      ret12m: number | null;
    }>;
  };
  productUsage: {
    totalQuotes: number;
    totalInvoices: number;
    paymentsProcessed: number;
    invoicePaidRate: number;
  };
  funnel: {
    signups: number;
    createdQuote: number;
    createdInvoice: number;
    collectedPayment: number;
    upgradedToPaid: number;
  };
  featureSignal: {
    usersSentEmail: number;
    usersHitEmailGate: number;
    usersEnabledPayments: number;
    usersProcessedPayment: number;
  };
  needsAttention: {
    paidInactive14d: Array<{ id: number; name: string; email: string; lastActiveAt: string | null }>;
    quotesNoInvoice: Array<{ id: number; name: string; email: string; quoteCount: number }>;
    invoicesNoPayment: Array<{ id: number; name: string; email: string; invoiceCount: number }>;
    heavyFreeUsers: Array<{ id: number; name: string; email: string; docCount: number }>;
    failedPaymentsList: Array<{ id: string; customer: string; email: string; amount: number; currency: string; date: string }>;
  };
}

function fmt(cents: number): string {
  return `$${(cents / 100).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function fmtDate(iso: string | null): string {
  if (!iso) return "Never";
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function getDateRange(range: DateRange, customFrom?: string, customTo?: string): { from: string; to: string } {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  const fmtD = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

  if (range === "custom") {
    return { from: customFrom ?? fmtD(new Date(now.getTime() - 30 * 86400000)), to: customTo ?? fmtD(now) };
  }
  if (range === "7d") return { from: fmtD(new Date(now.getTime() - 7 * 86400000)), to: fmtD(now) };
  if (range === "30d") return { from: fmtD(new Date(now.getTime() - 30 * 86400000)), to: fmtD(now) };
  if (range === "90d") return { from: fmtD(new Date(now.getTime() - 90 * 86400000)), to: fmtD(now) };
  if (range === "thisMonth") {
    const first = new Date(now.getFullYear(), now.getMonth(), 1);
    return { from: fmtD(first), to: fmtD(now) };
  }
  return { from: fmtD(new Date(now.getTime() - 30 * 86400000)), to: fmtD(now) };
}

type HealthStatus = "ok" | "degraded" | "error" | "checking";

interface HealthData {
  status: "ok" | "degraded";
  db: "ok" | "error";
}

function useServerHealth(intervalMs = 30000) {
  const [health, setHealth] = useState<HealthData | null>(null);
  const [status, setStatus] = useState<HealthStatus>("checking");
  const [lastChecked, setLastChecked] = useState<Date | null>(null);
  const [checking, setChecking] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const check = useCallback(async () => {
    setChecking(true);
    try {
      const res = await fetch(`${BASE}/api/health`, { credentials: "include" });
      const data: HealthData = await res.json();
      setHealth(data);
      setStatus(data.status === "ok" ? "ok" : "degraded");
    } catch {
      setHealth(null);
      setStatus("error");
    } finally {
      setLastChecked(new Date());
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    check();
    timerRef.current = setInterval(check, intervalMs);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [check, intervalMs]);

  return { health, status, lastChecked, checking, refresh: check };
}

function StatusBadge({ status, label }: { status: "ok" | "error" | "degraded" | "checking"; label: string }) {
  const colors: Record<string, { bg: string; text: string; dot: string }> = {
    ok:       { bg: "#10b98120", text: "#10b981", dot: "#10b981" },
    degraded: { bg: "#f59e0b20", text: "#f59e0b", dot: "#f59e0b" },
    error:    { bg: "#ef444420", text: "#ef4444", dot: "#ef4444" },
    checking: { bg: "hsl(var(--muted))", text: "hsl(var(--muted-foreground))", dot: "hsl(var(--muted-foreground))" },
  };
  const c = colors[status] ?? colors.checking;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: "0.375rem", background: c.bg, color: c.text, padding: "0.25rem 0.625rem", borderRadius: "9999px", fontSize: "0.8125rem", fontWeight: 600 }}>
      <span style={{ width: "0.5rem", height: "0.5rem", borderRadius: "50%", background: c.dot, display: "inline-block" }} />
      {label}
    </span>
  );
}

function ServerHealthSection() {
  const { health, status, lastChecked, checking, refresh } = useServerHealth();

  const serverStatus: "ok" | "degraded" | "error" | "checking" =
    status === "error" ? "error" : status === "ok" ? "ok" : status === "degraded" ? "degraded" : "checking";

  const dbStatus: "ok" | "error" | "checking" =
    !health ? (status === "error" ? "error" : "checking") : health.db === "ok" ? "ok" : "error";

  return (
    <Card style={{ marginBottom: "2rem" }}>
      <CardHeader style={{ paddingBottom: "0.5rem" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "0.5rem" }}>
          <CardTitle style={{ fontSize: "0.9375rem" }}>Server Health</CardTitle>
          <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
            {lastChecked && (
              <span style={{ fontSize: "0.75rem", color: "hsl(var(--muted-foreground))" }}>
                Checked {lastChecked.toLocaleTimeString()}
              </span>
            )}
            <button
              onClick={refresh}
              disabled={checking}
              style={{ display: "flex", alignItems: "center", gap: "0.25rem", background: "none", border: "1px solid hsl(var(--border))", borderRadius: "0.375rem", padding: "0.25rem 0.625rem", cursor: checking ? "not-allowed" : "pointer", fontSize: "0.75rem", color: "hsl(var(--muted-foreground))", opacity: checking ? 0.6 : 1 }}
            >
              <RefreshCw style={{ width: "0.75rem", height: "0.75rem", animation: checking ? "spin 1s linear infinite" : undefined }} />
              Refresh
            </button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "1.25rem" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", color: "hsl(var(--muted-foreground))", fontSize: "0.8125rem", fontWeight: 500 }}>
              <Server style={{ width: "1rem", height: "1rem" }} />
              API Server
            </div>
            <StatusBadge status={serverStatus} label={serverStatus === "ok" ? "Healthy" : serverStatus === "degraded" ? "Degraded" : serverStatus === "error" ? "Unreachable" : "Checking…"} />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", color: "hsl(var(--muted-foreground))", fontSize: "0.8125rem", fontWeight: 500 }}>
              <Database style={{ width: "1rem", height: "1rem" }} />
              Database
            </div>
            <StatusBadge status={dbStatus} label={dbStatus === "ok" ? "Connected" : dbStatus === "error" ? "Error" : "Checking…"} />
          </div>
        </div>
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </CardContent>
    </Card>
  );
}

function KpiCard({ title, value, sub, icon: Icon, color }: { title: string; value: string | number; sub?: string; icon: React.ElementType; color: string }) {
  return (
    <Card>
      <CardHeader style={{ paddingBottom: "0.375rem" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <CardTitle style={{ fontSize: "0.8125rem", fontWeight: 500, color: "hsl(var(--muted-foreground))" }}>{title}</CardTitle>
          <div style={{ width: "2.25rem", height: "2.25rem", borderRadius: "0.5rem", background: `${color}18`, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Icon style={{ width: "1.125rem", height: "1.125rem", color }} />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div style={{ fontSize: "1.75rem", fontWeight: 700 }}>{typeof value === "number" ? value.toLocaleString() : value}</div>
        {sub && <p style={{ fontSize: "0.75rem", color: "hsl(var(--muted-foreground))", marginTop: "0.125rem" }}>{sub}</p>}
      </CardContent>
    </Card>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h2 style={{ fontSize: "1.0625rem", fontWeight: 700, marginBottom: "1rem", paddingBottom: "0.5rem", borderBottom: "1px solid hsl(var(--border))" }}>{children}</h2>
  );
}

function UserRow({ user, sublabel }: { user: { id: number; name: string; email: string }; sublabel?: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0.625rem 0", borderBottom: "1px solid hsl(var(--border))" }}>
      <div>
        <div style={{ fontWeight: 500, fontSize: "0.875rem" }}>{user.name}</div>
        <div style={{ fontSize: "0.75rem", color: "hsl(var(--muted-foreground))" }}>{user.email}{sublabel ? ` · ${sublabel}` : ""}</div>
      </div>
      <Link href={`/admin/users/${user.id}`}>
        <button style={{ display: "flex", alignItems: "center", gap: "0.25rem", fontSize: "0.75rem", color: "#3b82f6", background: "none", border: "none", cursor: "pointer", padding: "0.25rem 0.5rem", borderRadius: "0.375rem" }}>
          View <ArrowRight style={{ width: "0.875rem", height: "0.875rem" }} />
        </button>
      </Link>
    </div>
  );
}

function retentionColor(val: number | null): string {
  if (val === null) return "hsl(var(--muted))";
  if (val >= 70) return "#10b981";
  if (val >= 40) return "#f59e0b";
  return "#ef4444";
}

function retentionText(val: number | null): string {
  if (val === null) return "–";
  return `${val}%`;
}

export default function AdminAnalytics() {
  usePageTitle("Admin – User Analytics");
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [range, setRange] = useState<DateRange>("30d");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");

  const fetchData = useCallback(() => {
    setLoading(true);
    setError("");
    const { from, to } = getDateRange(range, customFrom, customTo);
    fetch(`${BASE}/api/admin/analytics?from=${from}&to=${to}`, { credentials: "include" })
      .then(async (res) => {
        if (!res.ok) throw new Error("Failed to fetch analytics");
        return res.json();
      })
      .then((d) => { setData(d); setLoading(false); })
      .catch((err) => { setError(err.message); setLoading(false); });
  }, [range, customFrom, customTo]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const rangeOptions: { label: string; value: DateRange }[] = [
    { label: "Last 7 days", value: "7d" },
    { label: "Last 30 days", value: "30d" },
    { label: "Last 90 days", value: "90d" },
    { label: "This month", value: "thisMonth" },
    { label: "Custom", value: "custom" },
  ];

  const funnelSteps = data ? [
    { label: "Signups", count: data.funnel.signups, color: "#3b82f6" },
    { label: "Created Quote", count: data.funnel.createdQuote, color: "#8b5cf6" },
    { label: "Created Invoice", count: data.funnel.createdInvoice, color: "#06b6d4" },
    { label: "Collected Payment", count: data.funnel.collectedPayment, color: "#10b981" },
    { label: "Upgraded to Paid", count: data.funnel.upgradedToPaid, color: "#f59e0b" },
  ] : [];

  return (
    <div style={{ maxWidth: "1200px" }}>
      <div style={{ marginBottom: "1.5rem" }}>
        <h1 style={{ fontSize: "1.75rem", fontWeight: 700, marginBottom: "0.25rem" }}>User Analytics</h1>
        <p style={{ color: "hsl(var(--muted-foreground))", fontSize: "0.9375rem" }}>
          Comprehensive platform metrics and business intelligence
        </p>
      </div>

      <ServerHealthSection />

      <Card style={{ marginBottom: "1.75rem" }}>
        <CardContent style={{ paddingTop: "1rem" }}>
          <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "0.5rem" }}>
            <span style={{ fontSize: "0.875rem", fontWeight: 500, marginRight: "0.25rem" }}>Date range:</span>
            {rangeOptions.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setRange(opt.value)}
                style={{
                  padding: "0.375rem 0.875rem",
                  borderRadius: "9999px",
                  border: "1px solid",
                  borderColor: range === opt.value ? "#3b82f6" : "hsl(var(--border))",
                  background: range === opt.value ? "#3b82f6" : "transparent",
                  color: range === opt.value ? "#fff" : "hsl(var(--foreground))",
                  fontSize: "0.8125rem",
                  cursor: "pointer",
                  fontWeight: range === opt.value ? 600 : 400,
                }}
              >
                {opt.label}
              </button>
            ))}
            {range === "custom" && (
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginLeft: "0.5rem" }}>
                <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)}
                  style={{ padding: "0.25rem 0.5rem", border: "1px solid hsl(var(--border))", borderRadius: "0.375rem", fontSize: "0.8125rem" }} />
                <span style={{ fontSize: "0.875rem" }}>to</span>
                <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)}
                  style={{ padding: "0.25rem 0.5rem", border: "1px solid hsl(var(--border))", borderRadius: "0.375rem", fontSize: "0.8125rem" }} />
                <button onClick={fetchData}
                  style={{ padding: "0.25rem 0.75rem", background: "#3b82f6", color: "#fff", border: "none", borderRadius: "0.375rem", cursor: "pointer", fontSize: "0.8125rem" }}>
                  Apply
                </button>
              </div>
            )}
            {loading && <span style={{ fontSize: "0.8125rem", color: "hsl(var(--muted-foreground))" }}>Refreshing…</span>}
          </div>
        </CardContent>
      </Card>

      {error && (
        <div style={{ padding: "1rem", background: "hsl(var(--destructive) / 0.1)", borderRadius: "0.5rem", color: "hsl(var(--destructive))", marginBottom: "1.5rem" }}>
          {error}
        </div>
      )}

      {data && (
        <>
          <div style={{ marginBottom: "2rem" }}>
            <SectionTitle>Core Dashboard</SectionTitle>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "1rem", marginBottom: "1.5rem" }}>
              <KpiCard title="Total Users" value={data.billing.totalUsers} icon={Users} color="#3b82f6" sub="All registered accounts" />
              <KpiCard title="Active Users (7d)" value={data.billing.activeUsersLast7Days} icon={Activity} color="#10b981" sub="Active in last 7 days" />
              <KpiCard title="Paid Users" value={data.billing.paidUsers} icon={CreditCard} color="#8b5cf6" sub="Currently on Pro plan" />
              <KpiCard title="MRR" value={fmt(data.billing.mrrCents)} icon={DollarSign} color="#f59e0b" sub="Monthly recurring revenue" />
              <KpiCard title="ARR" value={fmt(data.billing.arrCents)} icon={TrendingUp} color="#06b6d4" sub="Annual recurring revenue" />
              <KpiCard title="New Signups" value={data.billing.newSignupsInRange} icon={Users} color="#3b82f6" sub="In selected range" />
              <KpiCard title="New Paid" value={data.billing.newPaidInRange} icon={CreditCard} color="#8b5cf6" sub="Upgraded in range" />
              <KpiCard title="Churn" value={data.billing.churnInRange} icon={TrendingDown} color="#ef4444" sub="Downgraded in range" />
              <KpiCard title="Revenue" value={fmt(data.billing.revenueTotalInRangeCents)} icon={DollarSign} color="#10b981" sub="Collected in range" />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.5rem", marginBottom: "1.5rem" }}>
              <Card>
                <CardHeader><CardTitle style={{ fontSize: "0.9375rem" }}>MRR Over Time (12 months)</CardTitle></CardHeader>
                <CardContent>
                  {data.billing.mrrByMonth.length === 0 ? (
                    <div style={{ height: "180px", display: "flex", alignItems: "center", justifyContent: "center", color: "hsl(var(--muted-foreground))", fontSize: "0.875rem" }}>
                      No data yet
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height={180}>
                      <LineChart data={data.billing.mrrByMonth}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                        <YAxis tickFormatter={(v) => `$${(v / 100).toFixed(0)}`} tick={{ fontSize: 11 }} />
                        <Tooltip formatter={(v: number) => [`$${(v / 100).toFixed(2)}`, "Revenue"]} />
                        <Line type="monotone" dataKey="mrr" stroke="#3b82f6" strokeWidth={2} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader><CardTitle style={{ fontSize: "0.9375rem" }}>Free vs Pro Users</CardTitle></CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={180}>
                    <BarChart data={data.billing.usersByPlan} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis type="number" tick={{ fontSize: 11 }} />
                      <YAxis dataKey="plan" type="category" tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Bar dataKey="count" name="Users" radius={[0, 4, 4, 0]}>
                        {data.billing.usersByPlan.map((entry) => (
                          <Cell key={entry.plan} fill={entry.plan === "pro" ? "#8b5cf6" : "#3b82f6"} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <CardTitle style={{ fontSize: "0.9375rem" }}>Failed Payments</CardTitle>
                  <span style={{ background: "#ef444420", color: "#ef4444", padding: "0.125rem 0.5rem", borderRadius: "9999px", fontSize: "0.75rem", fontWeight: 600 }}>
                    {data.billing.failedPayments.length}
                  </span>
                </div>
              </CardHeader>
              <CardContent>
                {data.billing.failedPayments.length === 0 ? (
                  <p style={{ fontSize: "0.875rem", color: "hsl(var(--muted-foreground))" }}>No failed payments found.</p>
                ) : (
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.8125rem" }}>
                    <thead>
                      <tr style={{ borderBottom: "1px solid hsl(var(--border))" }}>
                        {["Customer", "Email", "Amount", "Date", "Status"].map(h => (
                          <th key={h} style={{ textAlign: "left", padding: "0.5rem 0.75rem", fontWeight: 600, color: "hsl(var(--muted-foreground))" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {data.billing.failedPayments.map((fp) => (
                        <tr key={fp.id} style={{ borderBottom: "1px solid hsl(var(--border))" }}>
                          <td style={{ padding: "0.5rem 0.75rem" }}>{fp.customer || "—"}</td>
                          <td style={{ padding: "0.5rem 0.75rem" }}>{fp.email || "—"}</td>
                          <td style={{ padding: "0.5rem 0.75rem" }}>{fmt(fp.amount)}</td>
                          <td style={{ padding: "0.5rem 0.75rem" }}>{fmtDate(fp.date)}</td>
                          <td style={{ padding: "0.5rem 0.75rem" }}>
                            <span style={{ background: "#ef444420", color: "#ef4444", padding: "0.125rem 0.375rem", borderRadius: "0.25rem", fontSize: "0.75rem" }}>{fp.status}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </CardContent>
            </Card>
          </div>

          <div style={{ marginBottom: "2rem" }}>
            <SectionTitle>Advanced SaaS Metrics</SectionTitle>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "1rem", marginBottom: "1.5rem" }}>
              <KpiCard title="ARPU" value={fmt(data.advanced.arpu)} icon={DollarSign} color="#8b5cf6" sub="MRR ÷ paid users" />
              <KpiCard title="LTV" value={fmt(data.advanced.ltv)} icon={TrendingUp} color="#10b981" sub="ARPU ÷ churn rate" />
            </div>

            <Card>
              <CardHeader><CardTitle style={{ fontSize: "0.9375rem" }}>Monthly Cohort Retention</CardTitle></CardHeader>
              <CardContent>
                {data.advanced.cohortRetention.length === 0 ? (
                  <p style={{ fontSize: "0.875rem", color: "hsl(var(--muted-foreground))" }}>No cohort data available yet.</p>
                ) : (
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.8125rem" }}>
                      <thead>
                        <tr style={{ borderBottom: "1px solid hsl(var(--border))" }}>
                          {["Cohort", "Users", "1 Month", "2 Months", "3 Months", "6 Months", "12 Months"].map(h => (
                            <th key={h} style={{ textAlign: "left", padding: "0.5rem 0.75rem", fontWeight: 600, color: "hsl(var(--muted-foreground))" }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {data.advanced.cohortRetention.slice(-12).map((row) => (
                          <tr key={row.month} style={{ borderBottom: "1px solid hsl(var(--border))" }}>
                            <td style={{ padding: "0.5rem 0.75rem", fontWeight: 500 }}>{row.month}</td>
                            <td style={{ padding: "0.5rem 0.75rem" }}>{row.total}</td>
                            {[row.ret1m, row.ret2m, row.ret3m, row.ret6m, row.ret12m].map((val, i) => (
                              <td key={i} style={{ padding: "0.5rem 0.75rem" }}>
                                <span style={{ background: `${retentionColor(val)}22`, color: retentionColor(val), padding: "0.125rem 0.5rem", borderRadius: "0.25rem", fontWeight: 600 }}>
                                  {retentionText(val)}
                                </span>
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <div style={{ marginBottom: "2rem" }}>
            <SectionTitle>Product Usage</SectionTitle>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "1rem" }}>
              <KpiCard title="Total Quotes" value={data.productUsage.totalQuotes} icon={FileText} color="#3b82f6" sub="All time" />
              <KpiCard title="Total Invoices" value={data.productUsage.totalInvoices} icon={Receipt} color="#8b5cf6" sub="All time" />
              <KpiCard title="Payments via Platform" value={data.productUsage.paymentsProcessed} icon={CreditCard} color="#10b981" sub="Stripe Connect payments" />
              <KpiCard title="Invoice Paid Rate" value={`${data.productUsage.invoicePaidRate}%`} icon={TrendingUp} color="#f59e0b" sub="Paid invoices ÷ total" />
            </div>
          </div>

          <div style={{ marginBottom: "2rem" }}>
            <SectionTitle>Conversion Funnel</SectionTitle>
            <Card>
              <CardContent style={{ paddingTop: "1.25rem" }}>
                {funnelSteps.map((step, i) => {
                  const prev = funnelSteps[i - 1];
                  const dropOff = prev && prev.count > 0 ? Math.round(((prev.count - step.count) / prev.count) * 100) : null;
                  const pct = funnelSteps[0].count > 0 ? Math.round((step.count / funnelSteps[0].count) * 100) : 0;
                  return (
                    <div key={step.label} style={{ marginBottom: "0.875rem" }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.375rem" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                          <span style={{ width: "1.5rem", height: "1.5rem", background: step.color, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: "0.6875rem", fontWeight: 700 }}>{i + 1}</span>
                          <span style={{ fontWeight: 500, fontSize: "0.875rem" }}>{step.label}</span>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                          {dropOff !== null && (
                            <span style={{ fontSize: "0.75rem", color: "#ef4444" }}>↓ {dropOff}% drop-off</span>
                          )}
                          <span style={{ fontWeight: 700, fontSize: "0.9375rem" }}>{step.count.toLocaleString()}</span>
                          <span style={{ fontSize: "0.75rem", color: "hsl(var(--muted-foreground))", width: "3rem", textAlign: "right" }}>{pct}%</span>
                        </div>
                      </div>
                      <div style={{ height: "0.625rem", background: "hsl(var(--muted))", borderRadius: "9999px", overflow: "hidden" }}>
                        <div style={{ height: "100%", width: `${pct}%`, background: step.color, borderRadius: "9999px", transition: "width 0.4s ease" }} />
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          </div>

          <div style={{ marginBottom: "2rem" }}>
            <SectionTitle>Feature Signal</SectionTitle>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "1rem" }}>
              <KpiCard title="Sent Emails" value={data.featureSignal.usersSentEmail} icon={Mail} color="#3b82f6" sub="Unique users who sent emails" />
              <KpiCard title="Hit Email Gate" value={data.featureSignal.usersHitEmailGate} icon={MailX} color="#ef4444" sub="Tried to send, no Pro plan" />
              <KpiCard title="Enabled Payments" value={data.featureSignal.usersEnabledPayments} icon={CreditCard} color="#8b5cf6" sub="Stripe Connect activated" />
              <KpiCard title="Processed Payment" value={data.featureSignal.usersProcessedPayment} icon={Zap} color="#10b981" sub="Received payment via platform" />
            </div>
          </div>

          <div style={{ marginBottom: "2rem" }}>
            <SectionTitle>Needs Attention</SectionTitle>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.5rem" }}>
              <Card>
                <CardHeader>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                    <Clock style={{ width: "1rem", height: "1rem", color: "#ef4444" }} />
                    <CardTitle style={{ fontSize: "0.9375rem" }}>Paid Users Inactive 14+ Days</CardTitle>
                    <span style={{ marginLeft: "auto", background: "#ef444420", color: "#ef4444", padding: "0.125rem 0.5rem", borderRadius: "9999px", fontSize: "0.75rem", fontWeight: 600 }}>
                      {data.needsAttention.paidInactive14d.length}
                    </span>
                  </div>
                </CardHeader>
                <CardContent>
                  {data.needsAttention.paidInactive14d.length === 0 ? (
                    <p style={{ fontSize: "0.875rem", color: "hsl(var(--muted-foreground))" }}>All paid users are active.</p>
                  ) : (
                    data.needsAttention.paidInactive14d.map((u) => (
                      <UserRow key={u.id} user={u} sublabel={`Last active: ${fmtDate(u.lastActiveAt)}`} />
                    ))
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                    <FileText style={{ width: "1rem", height: "1rem", color: "#f59e0b" }} />
                    <CardTitle style={{ fontSize: "0.9375rem" }}>Quotes Without Invoice</CardTitle>
                    <span style={{ marginLeft: "auto", background: "#f59e0b20", color: "#f59e0b", padding: "0.125rem 0.5rem", borderRadius: "9999px", fontSize: "0.75rem", fontWeight: 600 }}>
                      {data.needsAttention.quotesNoInvoice.length}
                    </span>
                  </div>
                </CardHeader>
                <CardContent>
                  {data.needsAttention.quotesNoInvoice.length === 0 ? (
                    <p style={{ fontSize: "0.875rem", color: "hsl(var(--muted-foreground))" }}>No users in this state.</p>
                  ) : (
                    data.needsAttention.quotesNoInvoice.map((u) => (
                      <UserRow key={u.id} user={u} sublabel={`${u.quoteCount} quote(s), no invoice`} />
                    ))
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                    <Receipt style={{ width: "1rem", height: "1rem", color: "#8b5cf6" }} />
                    <CardTitle style={{ fontSize: "0.9375rem" }}>Invoices Without Payment</CardTitle>
                    <span style={{ marginLeft: "auto", background: "#8b5cf620", color: "#8b5cf6", padding: "0.125rem 0.5rem", borderRadius: "9999px", fontSize: "0.75rem", fontWeight: 600 }}>
                      {data.needsAttention.invoicesNoPayment.length}
                    </span>
                  </div>
                </CardHeader>
                <CardContent>
                  {data.needsAttention.invoicesNoPayment.length === 0 ? (
                    <p style={{ fontSize: "0.875rem", color: "hsl(var(--muted-foreground))" }}>No unpaid invoices found.</p>
                  ) : (
                    data.needsAttention.invoicesNoPayment.map((u) => (
                      <UserRow key={u.id} user={u} sublabel={`${u.invoiceCount} unpaid invoice(s)`} />
                    ))
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                    <AlertTriangle style={{ width: "1rem", height: "1rem", color: "#10b981" }} />
                    <CardTitle style={{ fontSize: "0.9375rem" }}>Heavy Free Users (5+ docs)</CardTitle>
                    <span style={{ marginLeft: "auto", background: "#10b98120", color: "#10b981", padding: "0.125rem 0.5rem", borderRadius: "9999px", fontSize: "0.75rem", fontWeight: 600 }}>
                      {data.needsAttention.heavyFreeUsers.length}
                    </span>
                  </div>
                </CardHeader>
                <CardContent>
                  {data.needsAttention.heavyFreeUsers.length === 0 ? (
                    <p style={{ fontSize: "0.875rem", color: "hsl(var(--muted-foreground))" }}>No heavy free users found.</p>
                  ) : (
                    data.needsAttention.heavyFreeUsers.map((u) => (
                      <UserRow key={u.id} user={u} sublabel={`${u.docCount} docs, still free`} />
                    ))
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </>
      )}

      {loading && !data && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "300px" }}>
          <div style={{ fontSize: "0.875rem", color: "hsl(var(--muted-foreground))" }}>Loading analytics…</div>
        </div>
      )}
    </div>
  );
}
